import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme
} from '@mui/material';
import {
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { SectionPaper, SectionTitle, StatusChip } from './styledComponents';

const ShippingInfoSection = ({ bol, onUpdate, canEdit = true }) => {
  const theme = useTheme();

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6">
        <ShippingIcon />
        Shipping Information
      </SectionTitle>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Carrier Name"
            fullWidth
            value={bol.carrier_name || ''}
            onChange={(e) => canEdit && onUpdate({ carrier_name: e.target.value })}
            placeholder="Enter freight carrier name"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Tracking Number"
            fullWidth
            value={bol.tracking_number || ''}
            onChange={(e) => canEdit && onUpdate({ tracking_number: e.target.value })}
            placeholder="Enter tracking number"
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'text' : 'not-allowed'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Ship Date"
              value={bol.ship_date ? dayjs(bol.ship_date) : null}
              onChange={(newValue) => canEdit && onUpdate({ ship_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
              sx={{ 
                width: '100%',
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expected Delivery Date"
              value={bol.expected_delivery_date ? dayjs(bol.expected_delivery_date) : null}
              onChange={(newValue) => canEdit && onUpdate({ expected_delivery_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
              sx={{ 
                width: '100%',
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Delivery Status</InputLabel>
            <Select
              value={bol.delivery_status || 'pending'}
              onChange={(e) => canEdit && onUpdate({ delivery_status: e.target.value })}
              label="Delivery Status"
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="in_transit">In Transit</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="delayed">Delayed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default ShippingInfoSection;